/** Tiny DOM helpers — the whole UI layer is plain DOM, no framework. */
import { trackGlass } from './glass.ts';

type Child = Node | string | number | null | undefined | false | Child[];
type Props = Record<string, unknown> | null;

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props?: Props, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (props) {
    for (const [k, v] of Object.entries(props)) {
      if (v === undefined || v === null || v === false) continue;
      if (k === 'class') el.className = String(v);
      else if (k === 'style' && typeof v === 'object') {
        for (const [prop, val] of Object.entries(v as Record<string, string>)) {
          if (prop.startsWith('--')) el.style.setProperty(prop, val);
          else (el.style as unknown as Record<string, string>)[prop] = val;
        }
      }
      else if (k === 'dataset' && typeof v === 'object') Object.assign(el.dataset, v);
      else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
      else if (k === 'html') el.innerHTML = String(v);
      else if (k in el && typeof v !== 'string') (el as unknown as Record<string, unknown>)[k] = v;
      else el.setAttribute(k, String(v));
    }
  }
  append(el, children);
  return el;
}

export function append(el: Node, children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    if (Array.isArray(c)) append(el, c);
    else el.appendChild(typeof c === 'string' || typeof c === 'number' ? document.createTextNode(String(c)) : c);
  }
}

/** All a control holds taken out — but its glass (ui/glass.ts: the canvas under its contents). */
export function clear(el: Element): void {
  for (const c of [...el.childNodes]) if (!isGlass(c)) c.remove();
}
/** A control's drawn glass: a canvas among its children that is no part of what it holds. */
export const isGlass = (n: Node): boolean => n instanceof HTMLCanvasElement && n.classList.contains('glass-gl');

/** Inline SVG icon from a path list (24×24 grid, currentColor, one optical weight). */
export function icon(paths: string, size = 22, cls = ''): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.75');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('class', cls ? `i ${cls}` : 'i');
  svg.innerHTML = paths;
  return svg;
}

const DOT = (x: number, y: number, r = 1.7) => `<circle cx="${x}" cy="${y}" r="${r}" fill="currentColor" stroke="none"/>`;
/** A four-pointed sparkle, its sides drawn in to its middle. */
const SPARKLE = (x: number, y: number, r: number) => `<path d="M${x} ${y - r}Q${x} ${y} ${x + r} ${y}Q${x} ${y} ${x} ${y + r}Q${x} ${y} ${x - r} ${y}Q${x} ${y} ${x} ${y - r}z" fill="currentColor" stroke-width="0.6"/>`;
const CLOUD = '<path d="M7 18.5a4.2 4.2 0 0 1-.6-8.36 5.8 5.8 0 0 1 11.2-1.1A4.75 4.75 0 0 1 17.2 18.5z"/>';
const LENS = '<circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.4 15.4L20 20"/>';
export const ICONS = {
  back: '<path d="M14.5 5.5L8 12l6.5 6.5" stroke-width="2.2"/>',
  undo: '<path d="M8.5 5L4 9.5 8.5 14"/><path d="M4.5 9.5H14a5.5 5.5 0 0 1 0 11h-3"/>',
  redo: '<path d="M15.5 5L20 9.5 15.5 14"/><path d="M19.5 9.5H10a5.5 5.5 0 0 0 0 11h3"/>',
  share: '<path d="M12 3.5v11"/><path d="M8 7.2l4-3.9 4 3.9"/><path d="M8.5 10.5H7.2A2.2 2.2 0 0 0 5 12.7v5.6a2.2 2.2 0 0 0 2.2 2.2h9.6a2.2 2.2 0 0 0 2.2-2.2v-5.6a2.2 2.2 0 0 0-2.2-2.2h-1.3"/>',
  more: DOT(5.5, 12) + DOT(12, 12) + DOT(18.5, 12),
  plus: '<path d="M12 5.5v13M5.5 12h13" stroke-width="2"/>',
  check: '<path d="M5.5 12.5l4.2 4.2L18.5 7.5" stroke-width="2"/>',
  close: '<path d="M6.5 6.5l11 11M17.5 6.5l-11 11" stroke-width="2"/>',
  trash: '<path d="M4.5 6.5h15"/><path d="M9.5 6.5V5A1.5 1.5 0 0 1 11 3.5h2A1.5 1.5 0 0 1 14.5 5v1.5"/><path d="M6.3 6.5l.8 12a2 2 0 0 0 2 1.9h5.8a2 2 0 0 0 2-1.9l.8-12"/><path d="M10 10.5v6M14 10.5v6"/>',
  test: '<circle cx="12" cy="12" r="8"/>' + DOT(12, 12, 3),
  // a work area: an outline with its light body
  area: '<path d="M5.2 16.8c-1.7-3.9-.6-8.6 3.5-10.4 4.3-1.9 10.2-.9 10.9 3.4.6 3.9-1.9 7.6-6.2 8.6-3.6.8-6.9 1.3-8.2-1.6z" fill="currentColor" fill-opacity=".22"/>',
  band: '<path d="M3.5 15.5c4.5-7 8.5 5 17-4.5" stroke-width="6.5" opacity=".28"/><path d="M3.5 15.5c4.5-7 8.5 5 17-4.5"/>',
  // a callout: words in a box, its arrow down onto what it is about
  callout: '<rect x="3.5" y="3.5" width="17" height="10" rx="3"/><path d="M7.8 8.5h8.4"/><path d="M12 13.5v6.3"/><path d="M9.3 17.3l2.7 2.6 2.7-2.6"/>',
  // the shapes: a square on a circle
  shapes: '<rect x="3.5" y="3.5" width="11" height="11" rx="2.2"/><path d="M14.5 9.4A5.6 5.6 0 1 1 9.4 14.5"/>',
  zoomIn: LENS + '<path d="M10.5 7.8v5.4M7.8 10.5h5.4"/>',
  zoomOut: LENS + '<path d="M7.8 10.5h5.4"/>',
  fit: '<path d="M4.5 9V6.7a2.2 2.2 0 0 1 2.2-2.2H9"/><path d="M15 4.5h2.3a2.2 2.2 0 0 1 2.2 2.2V9"/><path d="M19.5 15v2.3a2.2 2.2 0 0 1-2.2 2.2H15"/><path d="M9 19.5H6.7a2.2 2.2 0 0 1-2.2-2.2V15"/>',
  cloud: CLOUD,
  cloudCheck: CLOUD + '<path d="M9.3 13.5l2 2 3.6-3.9"/>',
  cloudUp: CLOUD + '<path d="M12 16.2v-4.8"/><path d="M9.9 13.4l2.1-2.1 2.1 2.1"/>',
  cloudAlert: CLOUD + '<path d="M12 10.8v3"/><path d="M12 16.3h.01" stroke-width="2.2"/>',
  cloudOff: CLOUD + '<path d="M4 4l16 16"/>',
  settings:
    '<path d="M10.37 5.19l.24-2.49a9.4 9.4 0 0 1 2.78 0l.24 2.49a7 7 0 0 1 2.03.84l1.93-1.59a9.4 9.4 0 0 1 1.97 1.97l-1.59 1.93a7 7 0 0 1 .84 2.03l2.49.24a9.4 9.4 0 0 1 0 2.78l-2.49.24a7 7 0 0 1-.84 2.03l1.59 1.93a9.4 9.4 0 0 1-1.97 1.97l-1.93-1.59a7 7 0 0 1-2.03.84l-.24 2.49a9.4 9.4 0 0 1-2.78 0l-.24-2.49a7 7 0 0 1-2.03-.84l-1.93 1.59a9.4 9.4 0 0 1-1.97-1.97l1.59-1.93a7 7 0 0 1-.84-2.03l-2.49-.24a9.4 9.4 0 0 1 0-2.78l2.49-.24a7 7 0 0 1 .84-2.03L4.44 6.41a9.4 9.4 0 0 1 1.97-1.97l1.93 1.59a7 7 0 0 1 2.03-.84z" stroke-width="1.6"/><circle cx="12" cy="12" r="3" stroke-width="1.6"/>',
  photo: '<rect x="3.5" y="5" width="17" height="14" rx="3"/><circle cx="9" cy="10" r="1.6"/><path d="M20.5 15.5l-4.2-4.2a1.4 1.4 0 0 0-2 0L7 18.8"/>',
  copy: '<rect x="8.5" y="8.5" width="11" height="11" rx="2.4"/><path d="M15.5 8.5V6.9a2.4 2.4 0 0 0-2.4-2.4H6.9a2.4 2.4 0 0 0-2.4 2.4v6.2a2.4 2.4 0 0 0 2.4 2.4h1.6"/>',
  folder: '<path d="M3.5 7.2A2.2 2.2 0 0 1 5.7 5h3.2a2 2 0 0 1 1.5.7l1 1.1a2 2 0 0 0 1.5.7h5.4a2.2 2.2 0 0 1 2.2 2.2v7.1a2.2 2.2 0 0 1-2.2 2.2H5.7a2.2 2.2 0 0 1-2.2-2.2z"/>',
  // arrows fanning out from one root (a callout pointing at several marks)
  arrows: '<rect x="3.5" y="3.5" width="11" height="6.5" rx="2"/><path d="M9 10L6 19.5"/><path d="M5 15.8l1 3.7 3-2.4"/><path d="M9 10l10.5 7.5"/><path d="M15.7 17.4l3.8.1-1.4-3.6"/>',
  // snapping: a magnet
  magnet: '<path d="M6 4.5h4.2v8.2a1.8 1.8 0 0 0 3.6 0V4.5H18v8.2a6 6 0 0 1-12 0z"/><path d="M6 8.6h4.2M13.8 8.6H18"/>',
  // a band made thinner, or wider
  thinner: '<path d="M4 12h16" stroke-width="1.4"/><path d="M12 3.5V9M9.8 6.8L12 9l2.2-2.2M12 20.5V15M9.8 17.2L12 15l2.2 2.2"/>',
  wider: '<path d="M4 12h16" stroke-width="3.2"/><path d="M12 8.5v-5M9.8 5.7L12 3.5l2.2 2.2M12 15.5v5M9.8 18.3l2.2 2.2 2.2-2.2"/>',
  // a retest: the arrow round again, the test in it
  retest: '<path d="M19 12a7 7 0 1 1-2.05-4.95"/><path d="M17.4 3.6v3.8h-3.8"/>' + DOT(12, 12, 2.2),
  // failing: a warning
  fail: '<path d="M10.3 4.9a2 2 0 0 1 3.4 0l7 12.1a2 2 0 0 1-1.7 3H5a2 2 0 0 1-1.7-3z"/><path d="M12 9.5v4.2"/>' + DOT(12, 16.6, 1.15),
  // back to where it goes by itself
  reset: '<path d="M4.8 12a7.2 7.2 0 1 0 2.1-5.1"/><path d="M4.6 4.4v3.4H8"/>',
  edit: '<path d="M4.5 19.5l1-4.2L16.2 4.6a1.85 1.85 0 0 1 2.6 0l.6.6a1.85 1.85 0 0 1 0 2.6L8.7 18.5z"/><path d="M14.5 6.4l3.1 3.1"/>',
  search: LENS,
  eye: '<path d="M2.8 12S6.2 5.8 12 5.8 21.2 12 21.2 12 17.8 18.2 12 18.2 2.8 12 2.8 12z"/><circle cx="12" cy="12" r="2.9"/>',
  list: '<path d="M9 7h10.5M9 12h10.5M9 17h10.5"/>' + DOT(5, 7, 1.2) + DOT(5, 12, 1.2) + DOT(5, 17, 1.2),
  key: '<circle cx="8" cy="15" r="4.2"/><path d="M11.2 12.2L19.5 4"/><path d="M15.5 8l2.6 2.6"/>',
  chevron: '<path d="M9.5 6l6 6-6 6" stroke-width="2"/>',
  /** The inspector's bubble: three sliders, each at a setting. */
  sliders: '<path d="M4.5 7h8.5M18.5 7h1M4.5 12h2.5M12.5 12h7M4.5 17h10M20 17h-.5"/><circle cx="15.75" cy="7" r="2.25"/><circle cx="9.75" cy="12" r="2.25"/><circle cx="17.25" cy="17" r="2.25"/>',
  /** The inspector's pill: three sliders on end, each at a setting, their knobs solid. */
  inspector: '<path d="M6 4v8.2M6 17.8V20M12 4v1.2M12 10.8V20M18 4v6.7M18 16.3V20"/><circle cx="6" cy="15" r="2.5" fill="currentColor" stroke="none"/><circle cx="12" cy="8" r="2.5" fill="currentColor" stroke="none"/><circle cx="18" cy="13.5" r="2.5" fill="currentColor" stroke="none"/>',
  /** Ready-made words: a list, its lines' starts dotted. */
  ready: '<path d="M9 7h11M9 12h11M9 17h11"/><circle cx="4.75" cy="7" r="1.25" fill="currentColor" stroke="none"/><circle cx="4.75" cy="12" r="1.25" fill="currentColor" stroke="none"/><circle cx="4.75" cy="17" r="1.25" fill="currentColor" stroke="none"/>',
  chevronDown: '<path d="M7 10l5 5 5-5" stroke-width="2"/>',
  // delete, as the keyboard's key: a tag pointing left with a cross in it
  backspace: '<path d="M9.3 5.5h9.2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9.3a2 2 0 0 1-1.6-.8L3.5 12l4.2-5.7a2 2 0 0 1 1.6-.8z"/><path d="M11.6 9.6l4.8 4.8M16.4 9.6l-4.8 4.8"/>',
  // the keyboard put away (the number pad's key while the keyboard is up)
  keyboardDown: '<rect x="3" y="4" width="18" height="11" rx="2.2"/><path d="M6.5 7.5h.01M9.5 7.5h.01M12.5 7.5h.01M15.5 7.5h.01M7.5 11h9" stroke-width="2"/><path d="M9 18.2l3 2.3 3-2.3"/>',
  // Return: the line taken in, and the field done
  enterKey: '<path d="M19 5.5v5.2a2.3 2.3 0 0 1-2.3 2.3H6"/><path d="M9.5 9.2 5.6 13l3.9 3.8"/>',
  pin: '<path d="M12 16.5V21"/><path d="M8.2 16.5h7.6l-.9-4.6 1.8-2V5.5H7.3v4.4l1.8 2z"/>',
  ruler: '<path d="M3.6 16.8L16.8 3.6l3.6 3.6L7.2 20.4z"/><path d="M8.3 12.1l1.9 1.9M11.2 9.2l1.9 1.9M14.1 6.3l1.9 1.9"/>',
  text: '<path d="M5.5 7V5.5h13V7"/><path d="M12 5.5v13"/><path d="M9.5 18.5h5"/>',
  minus: '<path d="M5.5 12h13" stroke-width="2"/>',
  // where a box's lines sit: from its left, its middle, its right
  alignLeft: '<path d="M4.5 6.5h15M4.5 10.5h9M4.5 14.5h15M4.5 18.5h9"/>',
  alignCenter: '<path d="M4.5 6.5h15M7.5 10.5h9M4.5 14.5h15M7.5 18.5h9"/>',
  alignRight: '<path d="M4.5 6.5h15M10.5 10.5h9M4.5 14.5h15M10.5 18.5h9"/>',
  // a box as wide as its words again: its sides drawn in to them
  fitWidth: '<path d="M4 5v14M20 5v14"/><path d="M7.5 12h9"/><path d="M10 9.5 7.5 12l2.5 2.5M14 9.5l2.5 2.5-2.5 2.5"/>',
  // a note: a page of a few lines, the last one short
  note: '<rect x="4.5" y="3.5" width="15" height="17" rx="3"/><path d="M8.5 8.5h7M8.5 12h7M8.5 15.5h4"/>',
  toolSquare: '<rect x="4.5" y="4.5" width="15" height="15" rx="1.6"/>',
  toolCircle: '<circle cx="12" cy="12" r="7.8"/>',
  tag: '<path d="M4.5 12.4V5.7a1.2 1.2 0 0 1 1.2-1.2h6.7a1.2 1.2 0 0 1 .85.35l6.9 6.9a1.2 1.2 0 0 1 0 1.7l-6.7 6.7a1.2 1.2 0 0 1-1.7 0l-6.9-6.9a1.2 1.2 0 0 1-.35-.85z"/>' + DOT(8.4, 8.4, 1.4),
  cut: '<circle cx="6.5" cy="17" r="2.8"/><circle cx="6.5" cy="7" r="2.8"/><path d="M8.9 8.5L20 17.5M8.9 15.5L20 6.5"/>',
  pointRound: '<path d="M5 19c0-7.7 6.3-14 14-14"/>' + DOT(9.1, 9.1, 2.4),
  pointCorner: '<path d="M5 19V5h14"/>' + DOT(5, 5, 2.4),
  // a corner rounded off: the arc touching both its sides
  pointRounded: '<path d="M5 19v-6.5A7.5 7.5 0 0 1 12.5 5H19"/>' + DOT(7.2, 7.2, 2.4),
  // the outline curving through a point
  pointCurve: '<path d="M3.5 17C7.5 17 8 7.5 12 7.5s4.5 9.5 8.5 9.5"/>' + DOT(12, 7.5, 2.4),
  // the finger drawing
  draw: '<path d="M3.5 15.5c2.2-5.5 4.3-5.5 5.8-.6s3.9 5.2 6.2.2c1.5-3.3 3-4.3 5-2.6"/>',
  // the Pen: a nib, its slit and breather hole
  pen: '<path d="M4.5 19.5l2.3-8.3 8.4-6.3 3.9 3.9-6.3 8.4z"/><path d="M4.5 19.5l5.7-5.7"/><circle cx="11.5" cy="12.5" r="1.5"/>',
  // an elevation shot as it is drawn: the ring where it was shot, its line out of the tag's side, the tag
  elev: '<circle cx="6" cy="17.3" r="2.5"/><path d="M11 8.4H9.3L7.4 15.2"/><rect x="11" y="4.6" width="9.6" height="7.6" rx="2"/><path d="M13.7 8.4h4.2"/>',
  // a palette of colours
  palette: '<path d="M12 3.5a8.5 8.5 0 0 0 0 17c1.3 0 1.9-.8 1.9-1.7 0-1.3-1.2-1.6-1.2-2.8 0-1 .8-1.7 1.9-1.7h2.2A3.7 3.7 0 0 0 20.5 10.6C20.5 6.6 16.7 3.5 12 3.5z"/>' + DOT(7.8, 11.6, 1.3) + DOT(10, 7.6, 1.3) + DOT(14.4, 7.6, 1.3),
  // Magic Mark: a wand and its sparks
  // Magic Mark: a highlighter laying its band — the mark — with a sparkle: it is made true to the plan
  magic: '<path d="M15.3 3.9a1.5 1.5 0 0 1 2.1 0l2.7 2.7a1.5 1.5 0 0 1 0 2.1l-7.4 7.4-4.8-4.8z"/><path d="M7.9 11.3 6.6 15l2.4 2.4 3.7-1.3"/><path d="M3.6 20.4h8.2" stroke-width="3" opacity=".4"/>' + SPARKLE(5.6, 5.6, 2.8),
};

export function el<T extends Element>(selector: string, root: ParentNode = document): T {
  const found = root.querySelector<T>(selector);
  if (!found) throw new Error(`Missing element ${selector}`);
  return found;
}

/** A short status pill under the top controls; a new message replaces the one showing. */
let toastTimer = 0;
/** When the message showing came (a message is always seen a moment, whatever puts it away early). */
let toastAt = 0;
const TOAST_LEAST_MS = 1600;
/**
 * Put away the message showing, if it is this one (a hint whose moment has passed) — never before it has been
 * seen for a moment: asked sooner, it goes when that moment is up (unless another message has taken its place).
 */
export function dismissToast(message: string): void {
  const host = document.getElementById('toast');
  if (!host || toastText(host) !== message || !host.classList.contains('show')) return;
  const left = TOAST_LEAST_MS - (performance.now() - toastAt);
  clearTimeout(toastTimer);
  if (left > 0) {
    toastTimer = window.setTimeout(() => toastText(host) === message && host.classList.remove('show'), left);
    return;
  }
  host.classList.remove('show');
}
/** The message pill's words: a span of their own (the pill's glass is never wiped with them: ui/glass.ts). */
function toastWords(host: HTMLElement): HTMLElement {
  let w = host.querySelector<HTMLElement>(':scope > .toast-text');
  if (!w) host.append((w = h('span', { class: 'toast-text' })));
  return w;
}
const toastText = (host: HTMLElement): string => host.querySelector(':scope > .toast-text')?.textContent ?? '';

let toastSwap: Animation | null = null;
export function toast(message: string, kind: 'info' | 'error' | 'success' = 'info', ms = 2400): void {
  let host = document.getElementById('toast');
  if (!host) {
    host = h('div', { id: 'toast', class: 'glass', role: 'status', 'aria-live': 'polite' }, h('span', { class: 'toast-text' }));
    document.body.append(host);
    trackGlass(host);
    void host.offsetWidth; // the first message slides in like every other
  }
  const el = host;
  const show = () => {
    toastWords(el).textContent = message;
    el.dataset.kind = kind;
    el.classList.add('show');
    toastAt = performance.now();
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => el.classList.remove('show'), Math.max(ms, TOAST_LEAST_MS));
  };
  toastSwap?.cancel();
  toastSwap = null;
  // one message in place of another: the pill fades, takes its new width unseen, and comes back — never a jump at the edges
  if (el.classList.contains('show') && toastText(el) !== message && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const out = (toastSwap = el.animate([{ opacity: 1 }, { opacity: 0, transform: 'scale(0.97)' }], { duration: 90, easing: 'ease-in', fill: 'forwards' }));
    out.onfinish = () => {
      if (toastSwap !== out) return;
      show();
      out.cancel();
      toastSwap = el.animate([{ opacity: 0, transform: 'scale(0.97)' }, { opacity: 1, transform: 'none' }], { duration: 200, easing: 'ease-out' });
    };
    return;
  }
  show();
}

/**
 * The selection thumb of a group of buttons: one capsule that slides under whichever button is
 * pressed (`aria-pressed`), so a choice moves rather than blinks. The thumb outlives the group's
 * buttons — refill the group with `fillGroup` — and keeps its place when the group is resized.
 */
export function thumbGroup<T extends HTMLElement>(group: T): T {
  group.classList.add('thumbed');
  group.prepend(h('span', { class: 'thumb', 'aria-hidden': 'true' }));
  // (resized — a chip added, say — it carries on to its place: a slide under way is never cut short)
  new ResizeObserver(() => placeThumb(group)).observe(group);
  return group;
}
/**
 * A parent's children made `nodes`, moving none of those already there: a group kept from one
 * render to the next (a strip's chips, with their thumb) stays where it is, so what it is doing —
 * a thumb sliding — goes on. (Moving an element, even to where it was, ends its transitions.)
 */
export function reconcile(parent: HTMLElement, nodes: (Node | null | false | undefined)[]): void {
  const want = nodes.filter((n): n is Node => !!n);
  const keep = new Set(want);
  for (const c of [...parent.childNodes]) if (!keep.has(c) && !isGlass(c)) c.remove();
  // (a glass canvas, first among them, is no part of the order)
  const skip = parent.firstChild && isGlass(parent.firstChild) ? 1 : 0;
  want.forEach((n, i) => {
    const at = parent.childNodes[i + skip];
    if (at !== n) parent.insertBefore(n, at ?? null);
  });
}
export function fillGroup(group: HTMLElement, children: Child[]): void {
  for (const c of [...group.children]) if (!c.classList.contains('thumb') && !isGlass(c)) c.remove();
  append(group, children);
  placeThumb(group);
}
export function placeThumb(group: HTMLElement, animate = true): void {
  const thumb = group.querySelector<HTMLElement>(':scope > .thumb');
  if (!thumb) return;
  const on = group.querySelector<HTMLElement>(':scope > [aria-pressed="true"]');
  if (!on || !on.offsetWidth) {
    thumb.classList.remove('on');
    return;
  }
  // it appears in place, and only ever slides from a place it has been seen in
  const still = !animate || !thumb.classList.contains('on');
  if (still) thumb.style.transition = 'none';
  thumb.style.width = `${on.offsetWidth}px`;
  thumb.style.height = `${on.offsetHeight}px`;
  thumb.style.translate = `${on.offsetLeft}px ${on.offsetTop}px`;
  thumb.classList.add('on');
  if (still) {
    void thumb.offsetWidth;
    thumb.style.transition = '';
  }
}

/** A segmented control: one of a few, the thumb sliding to the choice. */
export function segmented<V extends string>(options: [V, string][], value: V, onPick: (v: V) => void): HTMLElement {
  const el = thumbGroup(h('div', { class: 'seg', role: 'group' }));
  const btns = options.map(([v, label]) =>
    h('button', { type: 'button', 'aria-pressed': String(v === value), onClick: (e: Event) => {
      for (const b of btns) b.setAttribute('aria-pressed', String(b === e.currentTarget));
      placeThumb(el);
      haptic();
      onPick(v);
    } }, label),
  );
  el.append(...btns);
  return el;
}

/**
 * A light tap under the finger. Safari has no vibration API; on iPhone a `switch` checkbox ticks
 * when it is toggled, and toggling a hidden one through its label is the one way a page can ask
 * for that tick. Everywhere else this does nothing.
 */
const tickable = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0 && !('vibrate' in navigator);
export function haptic(): void {
  try {
    if (navigator.vibrate) {
      navigator.vibrate(8);
      return;
    }
    if (!tickable) return;
    const input = h('input', { type: 'checkbox', switch: '', tabindex: '-1' });
    const label = h('label', { 'aria-hidden': 'true', hidden: true }, input);
    document.head.append(label);
    label.click();
    label.remove();
  } catch {
    /* ignore */
  }
}
